import React, { useState, useEffect } from 'react';
import { useForm } from '../../../context/FormContext';
import FormField from '../../common/FormField/FormField';
import Accordion from '../../common/Accordion/Accordion';
import { validateSection, isSectionComplete } from '../../../utils/validation';
import './PepInformation.css';

const PepInformation = ({ data, errors, onSave, isLoading, isReadOnly }) => {
  const { actions } = useForm();
  const [formData, setFormData] = useState(data);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const handleChange = (field, value) => {
    if (isReadOnly) return;

    const newData = { ...formData, [field]: value };
    setFormData(newData);
    setHasChanges(true);

    // Update context immediately for real-time validation
    actions.updateSectionData('pepInformation', newData, 'in_progress');
  };

  const handleSave = () => {
    if (hasChanges) {
      // Validate the section
      const validationErrors = validateSection('pepInformation', formData);
      const isComplete = isSectionComplete('pepInformation', formData);

      // Set validation errors in context
      actions.setSectionErrors('pepInformation', validationErrors);

      // Determine status based on completion
      const status = isComplete ? 'completed' : 'in_progress';

      // Update section data with proper status
      actions.updateSectionData('pepInformation', formData, status);

      // Call the save handler
      onSave(formData);
      setHasChanges(false);
    }
  };

  const addPepPerson = () => {
    const newPerson = {
      id: Date.now().toString(),
      fullName: '',
      position: '',
      passport: '',
      nationality: '',
      dateOfBirth: '',
      reason: ''
    };

    const newPepPersons = [...(formData.pepPersons || []), newPerson];
    handleChange('pepPersons', newPepPersons);
  };

  const removePepPerson = (id) => {
    const newPepPersons = (formData.pepPersons || []).filter(person => person.id !== id);
    handleChange('pepPersons', newPepPersons);
  };

  const updatePepPerson = (id, field, value) => {
    const newPepPersons = (formData.pepPersons || []).map(person =>
      person.id === id ? { ...person, [field]: value } : person
    );
    handleChange('pepPersons', newPepPersons);
  };

  // Auto-save when user stops typing
  // Removed auto-saving - users must manually save sections

  return (
    <div className="pep-information">
      <div className="section-intro">
        <p>Please declare if any persons associated with your company are Politically Exposed Persons (PEPs).</p>
        <div className="pep-definition">
          <h4>What is a PEP?</h4>
          <p>A Politically Exposed Person is someone who holds or has held a prominent public function, including their family members and close associates.</p>
        </div>
      </div>

      <FormField
        label="PEP status declaration"
        required
        error={errors.hasPep}
        className="radio-field"
      >
        <div className="radio-group">
          <label className="radio-option">
            <input
              type="radio"
              name="hasPep"
              value="true"
              checked={formData.hasPep === true}
              onChange={() => handleChange('hasPep', true)}
              disabled={isReadOnly}
            />
            <span>Yes, there are PEPs associated with our company</span>
          </label>
          <label className="radio-option">
            <input
              type="radio"
              name="hasPep"
              value="false"
              checked={formData.hasPep === false}
              onChange={() => handleChange('hasPep', false)}
              disabled={isReadOnly}
            />
            <span>No, there are no PEPs associated with our company</span>
          </label>
        </div>
      </FormField>

      {formData.hasPep === true && (
        <div className="pep-persons-section">
          <div className="section-header">
            <h3>PEP Details</h3>
            {!isReadOnly && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addPepPerson}
              >
                Add PEP Person
              </button>
            )}
          </div>

          {(!formData.pepPersons || formData.pepPersons.length === 0) && (
            <div className="empty-state">
              <p>No PEP persons added yet. Click "Add PEP Person" to add details.</p>
            </div>
          )}

          <div className="accordion-group">
            {formData.pepPersons && formData.pepPersons.map((person, index) => (
              <Accordion
                key={person.id}
                title={`PEP Person ${index + 1}${person.fullName ? ` - ${person.fullName}` : ''}`}
                isOpen={false}
                className="compact"
                headerActions={
                  !isReadOnly && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removePepPerson(person.id)}
                      aria-label="Remove PEP person"
                    >
                      ×
                    </button>
                  )
                }
              >
                <div className="form-grid">
                  <FormField
                    label="Full name & surname"
                    required
                    className="grid-span-2"
                  >
                    <input
                      type="text"
                      className="form-input"
                      value={person.fullName || ''}
                      onChange={(e) => updatePepPerson(person.id, 'fullName', e.target.value)}
                      placeholder="Full name"
                      disabled={isReadOnly}
                    />
                  </FormField>

                  <FormField
                    label="Position in company"
                    required
                  >
                    <input
                      type="text"
                      className="form-input"
                      value={person.position || ''}
                      onChange={(e) => updatePepPerson(person.id, 'position', e.target.value)}
                      placeholder="Position"
                      disabled={isReadOnly}
                    />
                  </FormField>

                  <FormField
                    label="Passport number"
                    required
                  >
                    <input
                      type="text"
                      className="form-input"
                      value={person.passport || ''}
                      onChange={(e) => updatePepPerson(person.id, 'passport', e.target.value)}
                      placeholder="Passport number"
                      disabled={isReadOnly}
                    />
                  </FormField>

                  <FormField
                    label="Nationality"
                    required
                  >
                    <input
                      type="text"
                      className="form-input"
                      value={person.nationality || ''}
                      onChange={(e) => updatePepPerson(person.id, 'nationality', e.target.value)}
                      placeholder="Nationality"
                      disabled={isReadOnly}
                    />
                  </FormField>

                  <FormField
                    label="Date of birth"
                    required
                  >
                    <input
                      type="date"
                      className="form-input"
                      value={person.dateOfBirth || ''}
                      onChange={(e) => updatePepPerson(person.id, 'dateOfBirth', e.target.value)}
                      disabled={isReadOnly}
                    />
                  </FormField>

                  <FormField
                    label="Reason for being PEP"
                    required
                    className="grid-span-2"
                  >
                    <input
                      type="text"
                      className="form-input"
                      value={person.reason || ''}
                      onChange={(e) => updatePepPerson(person.id, 'reason', e.target.value)}
                      placeholder="e.g., Government official, family member of politician"
                      disabled={isReadOnly}
                    />
                  </FormField>
                </div>
              </Accordion>
            ))}
          </div>

          {errors.pepPersons && (
            <div className="form-error">
              {errors.pepPersons}
            </div>
          )}
        </div>
      )}

      {!isReadOnly && (
        <div className="section-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!hasChanges || isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : (
              'Save Section'
            )}
          </button>

          {hasChanges && (
            <span className="unsaved-indicator">
              You have unsaved changes
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default PepInformation;
